
import type { Ref} from 'vue';
import { shallowRef, watch } from 'vue';
import { useRouter, type LocationQuery, type LocationQueryRaw } from 'vue-router';

/**
 * @type QueryParams: are the values to send to the server as a URL query value.
 * @type PaginationParams: are values from query URL, cleaned to use with the table
 */
export type ParamsToQueryFn<QueryParams, PaginationParams, DefaultValues = unknown> = (
  params: PaginationParams,
  defaultValues?: DefaultValues,
) => QueryParams;
export type ParamsFromQueryFn<PaginationParams, DefaultValues = unknown> = (
  query: LocationQuery,
  defaultValues?: DefaultValues,
) => PaginationParams;

type useTableReturn<PaginationParams> = {
  query: Ref<PaginationParams>;
  setQuery: (params: PaginationParams) => void;
};

/**
 *
 * @param getParamsToQuery to get the param to update the query in URL
 * @param getParamsFromQuery to get the params form the query URL to use in the table
 * @param setURL
 * @param defaultValues
 * @param readFromURL
 * @returns
 */
function useTableRouter<QueryParams, PaginationParams, DefaultValues = unknown>(
  getParamsToQuery: ParamsToQueryFn<QueryParams, PaginationParams, DefaultValues>,
  getParamsFromQuery: ParamsFromQueryFn<PaginationParams, DefaultValues>,
  setURL?: boolean,
  defaultValues?: DefaultValues,
  readFromURL?: boolean,
): useTableReturn<PaginationParams> {
  const router = useRouter();
  const shouldReadFromURL = readFromURL !== undefined ? readFromURL : true;

  const query = shallowRef<PaginationParams>(
    shouldReadFromURL
      ? getParamsFromQuery(router.currentRoute.value.query, defaultValues)
      : ({} as PaginationParams),
  );

  if (shouldReadFromURL) {
    watch(router.currentRoute, () => {
      query.value = getParamsFromQuery(router.currentRoute.value.query, defaultValues);
    });
  }

  const setQuery = (params: PaginationParams) => {
    if (setURL) {
      const newQuery = getParamsToQuery(params, defaultValues) as unknown as LocationQueryRaw;
      router.push({ query: newQuery });
    } else {
      query.value = params;
    }
  };

  return {
    query,
    setQuery,
  };
}

export default useTableRouter;
